// pages/admin/SettingsPage.tsx
/**
 * @description Admin settings page with profile, password, and notification preferences
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  Lock,
  Bell,
  Mail,
  CheckCircle,
  Building,
  Smartphone,
  Wrench,
  Home,
  ChevronRight
} from 'lucide-react';
import { AdminLayout } from '../../components/layout';
import { Button, Input } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../context/AuthContext';
import { updateMyProfile } from '../../lib/api';

type SettingsTab = 'profile' | 'password' | 'notifications' | 'system';

export function SettingsPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const isSuper = user?.role === 'super_admin';

  const tabs = [
    { id: 'profile' as const, label: 'Profile', icon: User, superOnly: false },
    { id: 'password' as const, label: 'Password', icon: Lock, superOnly: false },
    { id: 'notifications' as const, label: 'Notifications', icon: Bell, superOnly: false },
    { id: 'system' as const, label: 'System', icon: Wrench, superOnly: true },
  ].filter(tab => !tab.superOnly || isSuper);

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-4xl">
        {/* Page Header */}
        <div className="mb-6 lg:mb-8">
          <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">Settings</h1>
          <p className="text-sm lg:text-base text-gray-500 mt-1">
            Manage your account settings and preferences
          </p>
        </div>

        {/* Tabs - Horizontal scroll on mobile */}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-full sm:w-fit mb-6 lg:mb-8 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg min-w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 lg:px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <tab.icon size={16} />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-6">
          {activeTab === 'profile' && (
            <ProfileSettings showToast={showToast} user={user} />
          )}
          {activeTab === 'password' && (
            <PasswordSettings showToast={showToast} />
          )}
          {activeTab === 'notifications' && (
            <NotificationSettings showToast={showToast} />
          )}
          {activeTab === 'system' && (
            <SystemSettings />
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function SystemSettings() {
  const systemLinks = [
    {
      to: '/admin/unit-types',
      icon: Home,
      title: 'Unit Types',
      description: 'Manage unit type classifications (Bachelor, 1-BR, 2-BR, etc.)',
    },
  ];

  return (
    <div className="space-y-5 lg:space-y-6">
      <div>
        <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-1">System Configuration</h3>
        <p className="text-xs lg:text-sm text-gray-500">
          Manage global settings and lookup tables
        </p>
      </div>

      <div className="space-y-2">
        {systemLinks.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors group"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
              <link.icon size={20} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 group-hover:text-primary transition-colors">
                {link.title}
              </p>
              <p className="text-xs text-gray-500">{link.description}</p>
            </div>
            <ChevronRight size={18} className="text-gray-400 group-hover:text-primary transition-colors" />
          </Link>
        ))}
      </div>
    </div>
  );
}

interface SettingsProps {
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  user?: { full_name: string | null; email: string; role: string; phone?: string | null; timezone?: string | null } | null;
}

function ProfileSettings({ showToast, user }: SettingsProps) {
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    timezone: user?.timezone || 'America/Toronto',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateMyProfile({
        full_name: formData.full_name,
        phone: formData.phone,
        timezone: formData.timezone,
      });
      if (result.error) {
        showToast(result.error, 'error');
      } else {
        showToast('Profile updated successfully', 'success');
      }
    } catch {
      showToast('Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 lg:space-y-6">
      <div>
        <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-1">Profile Information</h3>
        <p className="text-xs lg:text-sm text-gray-500">
          Update your personal information and contact details
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
        <Input
          label="Full Name"
          value={formData.full_name}
          onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
          placeholder="Your full name"
        />
        <Input
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          placeholder="you@example.com"
        />
        <Input
          label="Phone Number"
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
          placeholder="(555) 123-4567"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Timezone
          </label>
          <select
            value={formData.timezone}
            onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="America/Toronto">Eastern Time (Toronto)</option>
            <option value="America/Vancouver">Pacific Time (Vancouver)</option>
            <option value="America/Edmonton">Mountain Time (Edmonton)</option>
            <option value="America/Winnipeg">Central Time (Winnipeg)</option>
          </select>
        </div>
      </div>

      {/* Role Badge */}
      <div className="p-3 lg:p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Building size={18} className="text-primary lg:hidden" />
            <Building size={20} className="text-primary hidden lg:block" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {user?.role === 'super_admin' ? 'Super Admin' : 'Leasing Agent'}
            </p>
            <p className="text-xs text-gray-500">
              {user?.role === 'super_admin'
                ? 'Full access to all buildings and settings'
                : 'Access to assigned buildings only'}
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-gray-100">
        <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

function PasswordSettings({ showToast }: Pick<SettingsProps, 'showToast'>) {
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (formData.new_password !== formData.confirm_password) {
      showToast('Passwords do not match', 'error');
      return;
    }
    if (formData.new_password.length < 8) {
      showToast('Password must be at least 8 characters', 'error');
      return;
    }

    setSaving(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    setSaving(false);
    setFormData({ current_password: '', new_password: '', confirm_password: '' });
    showToast('Password updated successfully', 'success');
  };

  return (
    <div className="space-y-5 lg:space-y-6">
      <div>
        <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-1">Change Password</h3>
        <p className="text-xs lg:text-sm text-gray-500">
          Update your password to keep your account secure
        </p>
      </div>

      <div className="max-w-md space-y-3 lg:space-y-4">
        <Input
          label="Current Password"
          type="password"
          value={formData.current_password}
          onChange={(e) => setFormData(prev => ({ ...prev, current_password: e.target.value }))}
          placeholder="Enter current password"
        />
        <Input
          label="New Password"
          type="password"
          value={formData.new_password}
          onChange={(e) => setFormData(prev => ({ ...prev, new_password: e.target.value }))}
          placeholder="Enter new password"
        />
        <Input
          label="Confirm New Password"
          type="password"
          value={formData.confirm_password}
          onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
          placeholder="Confirm new password"
        />
      </div>

      {/* Password Requirements */}
      <div className="p-3 lg:p-4 bg-blue-50 rounded-lg">
        <p className="text-sm font-medium text-blue-900 mb-2">Password Requirements</p>
        <ul className="text-xs lg:text-sm text-blue-700 space-y-1">
          <li className="flex items-center gap-2">
            <CheckCircle size={14} className="flex-shrink-0" />
            At least 8 characters long
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle size={14} className="flex-shrink-0" />
            Contains a mix of letters and numbers
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle size={14} className="flex-shrink-0" />
            Includes at least one special character
          </li>
        </ul>
      </div>

      <div className="flex justify-end pt-4 border-t border-gray-100">
        <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
          {saving ? 'Updating...' : 'Update Password'}
        </Button>
      </div>
    </div>
  );
}

function NotificationSettings({ showToast }: Pick<SettingsProps, 'showToast'>) {
  const [settings, setSettings] = useState({
    email_new_inquiry: true,
    email_new_application: true,
    email_viewing_request: true,
    email_weekly_digest: false,
    push_new_inquiry: true,
    push_new_application: true,
    push_viewing_request: false,
  });
  const [saving, setSaving] = useState(false);

  const handleToggle = (key: keyof typeof settings) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    setSaving(false);
    showToast('Notification preferences saved', 'success');
  };

  const NotificationToggle = ({
    label,
    description,
    checked,
    onChange
  }: {
    label: string;
    description: string;
    checked: boolean;
    onChange: () => void;
  }) => (
    <label className="flex items-center justify-between py-3 cursor-pointer gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <button
        type="button"
        onClick={onChange}
        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
          checked ? 'bg-primary' : 'bg-gray-200'
        }`}
      >
        <span
          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
            checked ? 'translate-x-5' : ''
          }`}
        />
      </button>
    </label>
  );

  return (
    <div className="space-y-5 lg:space-y-6">
      <div>
        <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-1">Notification Preferences</h3>
        <p className="text-xs lg:text-sm text-gray-500">
          Choose how you want to be notified about activity
        </p>
      </div>

      {/* Email Notifications */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Mail size={18} className="text-gray-500" />
          <h4 className="text-sm lg:text-base font-medium text-gray-900">Email Notifications</h4>
        </div>
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          <div className="px-3 lg:px-4">
            <NotificationToggle
              label="New Inquiries"
              description="Get notified when someone sends an inquiry"
              checked={settings.email_new_inquiry}
              onChange={() => handleToggle('email_new_inquiry')}
            />
          </div>
          <div className="px-3 lg:px-4">
            <NotificationToggle
              label="New Applications"
              description="Get notified when someone submits an application"
              checked={settings.email_new_application}
              onChange={() => handleToggle('email_new_application')}
            />
          </div>
          <div className="px-3 lg:px-4">
            <NotificationToggle
              label="Viewing Requests"
              description="Get notified when someone requests a viewing"
              checked={settings.email_viewing_request}
              onChange={() => handleToggle('email_viewing_request')}
            />
          </div>
          <div className="px-3 lg:px-4">
            <NotificationToggle
              label="Weekly Digest"
              description="Receive a weekly summary of activity"
              checked={settings.email_weekly_digest}
              onChange={() => handleToggle('email_weekly_digest')}
            />
          </div>
        </div>
      </div>

      {/* Push Notifications */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Smartphone size={18} className="text-gray-500" />
          <h4 className="text-sm lg:text-base font-medium text-gray-900">Push Notifications</h4>
        </div>
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          <div className="px-3 lg:px-4">
            <NotificationToggle
              label="New Inquiries"
              description="Instant push notification for new inquiries"
              checked={settings.push_new_inquiry}
              onChange={() => handleToggle('push_new_inquiry')}
            />
          </div>
          <div className="px-3 lg:px-4">
            <NotificationToggle
              label="New Applications"
              description="Instant push notification for new applications"
              checked={settings.push_new_application}
              onChange={() => handleToggle('push_new_application')}
            />
          </div>
          <div className="px-3 lg:px-4">
            <NotificationToggle
              label="Viewing Requests"
              description="Instant push notification for viewing requests"
              checked={settings.push_viewing_request}
              onChange={() => handleToggle('push_viewing_request')}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-gray-100">
        <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );
}
